/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState, type RefObject } from 'react';

/** After this long without an iframe load (e.g. Terrific answered 404), stop waiting. */
export const TERRIFIC_LOAD_TIMEOUT_MS = 15_000;

/**
 * Tracks whether the Terrific SDK has finished loading the embed inside `containerRef`.
 *
 * The SDK appends a `<terrific-embedding>` whose iframe lives in its light DOM (carousels) or in
 * its shadow root (live sessions). `load` doesn't bubble out of either, so every iframe found is
 * listened to directly. Resolves to `true` on the first iframe load, or after
 * {@link TERRIFIC_LOAD_TIMEOUT_MS} so a failed embed doesn't keep a placeholder forever.
 */
export function useTerrificLoaded(containerRef: RefObject<HTMLElement | null>, enabled: boolean): boolean {
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container) return;

        const done = () => setLoaded(true);
        const watched = new WeakSet<Node>();
        const iframes = new Set<HTMLIFrameElement>();
        const observer = new MutationObserver(() => scan(container));

        function scan(root: ParentNode) {
            root.querySelectorAll('iframe').forEach((iframe) => {
                if (iframes.has(iframe)) return;
                iframes.add(iframe);
                iframe.addEventListener('load', done, { once: true });
            });
            root.querySelectorAll('*').forEach((element) => {
                const shadow = element.shadowRoot;
                if (shadow && !watched.has(shadow)) {
                    watched.add(shadow);
                    observer.observe(shadow, { childList: true, subtree: true });
                    scan(shadow);
                }
            });
        }

        observer.observe(container, { childList: true, subtree: true });
        scan(container);
        const timeout = window.setTimeout(done, TERRIFIC_LOAD_TIMEOUT_MS);

        return () => {
            observer.disconnect();
            window.clearTimeout(timeout);
            iframes.forEach((iframe) => iframe.removeEventListener('load', done));
        };
    }, [containerRef, enabled]);

    return loaded;
}
